import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaHeart, FaShieldAlt, FaCheck, FaClock, FaUsers, FaLightbulb, FaBookOpen, FaHandsHelping } from 'react-icons/fa';
import { motion } from 'framer-motion';
import SEO from '../../components/SEO.jsx';
import muscleDiscomfortImage from '../../components/assets/Staff/Muscle Discomfort1.jpg';

const MuscleJointSpine = () => {
  const [activeSection, setActiveSection] = useState('overview');
  
  // Refs for smooth scrolling
  const overviewRef = useRef(null);
  const understandingRef = useRef(null);
  const musculoskeletalRef = useRef(null);
  const researchRef = useRef(null);
  const summaryRef = useRef(null);

  const scrollToSection = (sectionId) => {
    const refs = {
      'overview': overviewRef,
      'understanding': understandingRef,
      'musculoskeletal': musculoskeletalRef,
      'research': researchRef,
      'summary': summaryRef
    };
    
    const targetRef = refs[sectionId];
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
      setActiveSection(sectionId);
    }
  };

  // Scroll spy effect to highlight active section
  useEffect(() => {
    const handleScroll = () => {
      const refs = [
        { id: 'overview', ref: overviewRef },
        { id: 'understanding', ref: understandingRef },
        { id: 'musculoskeletal', ref: musculoskeletalRef },
        { id: 'research', ref: researchRef },
        { id: 'summary', ref: summaryRef }
      ];

      const scrollPosition = window.scrollY + 100; // Offset for sticky nav

      for (let i = refs.length - 1; i >= 0; i--) {
        const { id, ref } = refs[i];
        if (ref.current && ref.current.offsetTop <= scrollPosition) {
          setActiveSection(id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sections = [
    { id: 'overview', label: 'Overview', icon: FaHeart },
    { id: 'understanding', label: 'Understanding', icon: FaBookOpen },
    { id: 'musculoskeletal', label: 'Musculoskeletal Support', icon: FaHandsHelping },
    { id: 'research', label: 'Selected Research', icon: FaLightbulb },
    { id: 'summary', label: 'In Summary', icon: FaCheck }
  ];

  const musculoskeletalBullets = [
    "experience long-term or recurring discomfort despite conventional management",
    "notice links between stress, tension and physical pain",
    "want a gentle, non-invasive method alongside physiotherapy or medical care",
    "are looking for a personalised, whole-body approach"
  ];

  const studyOutcomes = [
    { label: "Overall reported effectiveness (very good to satisfactory)", value: "83.3%" },
    { label: "Reported effectiveness for pain-related complaints", value: "85.9%" },
    { label: "Reported effectiveness for allergies", value: "88.2%" },
    { label: "Reported effectiveness for infections", value: "96.1%" }
  ];

  return (
    <div className="pt-20 min-h-screen bg-gradient-to-br from-white via-yellow/5 to-gold/5">
      <SEO title="Bioresonance for Muscle, Joint and Spine" description="Bioresonance therapy for muscle, joint and spine discomfort at Bioresonance Leicester. Complementary pain support. Book at 108 New Walk." path="/therapy/bioresonance-therapy/muscle-joint-spine" />
      {/* Breadcrumb */}
      <nav className="container mx-auto px-4 py-6">
        <ol className="flex items-center space-x-2 text-sm text-dark-grey">
          <li><Link to="/" className="hover:text-gold transition-colors">Home</Link></li>
          <li className="text-gold">/</li>
          <li><Link to="/therapy" className="hover:text-gold transition-colors">Treatments</Link></li>
          <li className="text-gold">/</li>
          <li><Link to="/therapy/bioresonance-therapy" className="hover:text-gold transition-colors">Bioresonance Therapy</Link></li>
          <li className="text-gold">/</li>
          <li className="text-gold font-semibold">Muscle, Joint and Spine</li>
        </ol>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-blue/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* Content */}
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <FaHeart className="mr-2" />
                Bioresonance Sub-Treatment
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-dark-grey leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
                Muscle, Joint and Spine Discomfort
              </h1>
              
              <p className="text-xl text-dark-grey leading-relaxed">
                Supporting mobility, comfort and balance in a complementary way
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                Ongoing muscle, joint and spine discomfort is one of the most common reasons people experience a reduced quality of life. Pain, stiffness or limited mobility can affect daily activities, sleep, work performance and emotional wellbeing.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                These concerns are no longer limited to older adults. Increasing numbers of younger individuals report persistent discomfort related to stress, prolonged sitting, poor posture, repetitive movements or lack of physical activity.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                While conventional care plays an essential role, some people seek additional, non-invasive ways to support their body alongside standard medical approaches.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Bioresonance is one such option, applied in practice as a frequency-based method.
              </p>

              <div className="flex flex-wrap gap-4 pt-4">
                <div className="flex items-center text-sm text-dark-grey">
                  <FaClock className="text-gold mr-2" />
                  <span>60-90 minutes</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaShieldAlt className="text-gold mr-2" />
                  <span>Non-invasive</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaUsers className="text-gold mr-2" />
                  <span>All ages</span>
                </div>
              </div>
            </motion.div>

            {/* Image Placeholder */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative group">
                {/* Decorative background shapes */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-gold/10 rounded-full opacity-60 group-hover:scale-110 transition-transform duration-500"></div>
                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-blue/20 rounded-full opacity-40 group-hover:scale-125 transition-transform duration-700"></div>
                
                {/* Main image container */}
                <div className="relative p-4">
                  <div className="relative overflow-hidden rounded-3xl h-96">
                    <img
                      src={muscleDiscomfortImage}
                      alt="Muscle, joint and spine support"
                      className="absolute inset-0 w-full h-full object-cover object-bottom rounded-3xl"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-3xl"></div>
                  </div>
                  
                  {/* Creative border */}
                  <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform rotate-3 group-hover:rotate-0 transition-transform duration-500"></div>
                  <div className="absolute inset-2 border-2 border-white/50 rounded-3xl transform -rotate-1 group-hover:rotate-0 transition-transform duration-500"></div>
                </div>
                
                {/* Floating elements */}
                <div className="absolute top-8 right-8 w-3 h-3 bg-gold rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100"></div>
                <div className="absolute bottom-8 left-8 w-2 h-2 bg-blue rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="hidden md:block py-8 bg-white/50 backdrop-blur-sm sticky top-20 z-30" aria-label="On this page">
        <div className="container mx-auto px-4">
          <nav className="flex flex-wrap justify-center gap-2" aria-label="Page sections">
            {sections.map((section) => (
              <button
                type="button"
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`flex items-center px-4 py-2 rounded-full font-semibold transition-all duration-300 ${
                  activeSection === section.id
                    ? 'bg-gold text-black shadow-lg transform scale-105'
                    : 'bg-white text-dark-grey hover:bg-gold/20 hover:text-gold border border-grey'
                }`}
                aria-current={activeSection === section.id ? 'location' : undefined}
              >
                <section.icon className="mr-2 text-sm" />
                {section.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Overview Section - anchor for nav */}
      <section ref={overviewRef} className="py-4" aria-hidden="true" />

      {/* Understanding pain from a holistic perspective */}
      <section ref={understandingRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Understanding pain from a holistic perspective
              </h2>
            </div>

            <div className="space-y-6 text-gray-600 leading-relaxed">
              <p>
                Muscle, joint and spinal discomfort can arise from many contributing factors, including physical strain, injuries, inflammatory processes, autoimmune activity, lifestyle habits, emotional stress or long-term overload of the nervous system.
              </p>
              <p>
                Commonly reported conditions include rheumatoid arthritis, degenerative spine changes, sciatica, cervical tension, knee discomfort and generalised musculoskeletal pain.
              </p>
              <p>
                From a holistic perspective, pain is not viewed solely as a mechanical issue, but as a complex interaction involving the nervous system, immune response, emotional stress and overall regulatory balance within the body.
              </p>
              <p>
                Within complementary wellness settings, this frequency-based approach does not diagnose the cause of pain and does not replace medical or physiotherapy care. Instead, it is applied to support balance, help reduce stress load and assist the body's natural self-regulatory processes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Musculoskeletal support using frequency-based methods */}
      <section ref={musculoskeletalRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Musculoskeletal support using frequency-based methods
              </h2>
            </div>

            <div className="space-y-6 text-gray-600 leading-relaxed mb-8">
              <p>
                Sessions using this approach are personalised and informed by the individual's unique responses. The method does not involve medication, physical manipulation or invasive procedures.
              </p>
              <p className="font-semibold text-dark-grey">Some individuals choose this form of support when they:</p>
              <ul className="space-y-3 list-none">
                {musculoskeletalBullets.map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <span className="text-gold mt-1">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Selected observational research */}
      <section ref={researchRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Selected observational research
              </h2>
            </div>

            <div className="space-y-6 text-gray-600 leading-relaxed mb-8">
              <p>
                One notable retrospective observational study examined the use of MORA bioresonance in individuals who had not responded to conventional approaches.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-grey/20 mb-8">
              <p className="font-semibold text-dark-grey mb-2">Study title:</p>
              <p className="text-gray-600 mb-4">
                Retrospective surgery study of the therapeutic effectiveness of MORA bioresonance therapy with conventional therapy resistant individuals suffering from allergies, pain and infection diseases
              </p>
              <p className="font-semibold text-dark-grey mb-2">Authors:</p>
              <p className="text-gray-600 mb-4">Eckart Herrmann, Michael Galle</p>
              <p className="font-semibold text-dark-grey mb-2">Published in:</p>
              <p className="text-gray-600 mb-6">European Journal of Integrative Medicine, September 2011</p>

              <p className="font-semibold text-dark-grey mb-2">Study overview:</p>
              <div className="space-y-2 text-gray-600 mb-6">
                <p><span className="font-medium text-dark-grey">Participants:</span> individuals resistant to conventional treatments</p>
                <p><span className="font-medium text-dark-grey">Conditions addressed:</span> internal, orthopaedic and neurological complaints</p>
                <p><span className="font-medium text-dark-grey">Methods:</span> MORA bioresonance, sometimes combined with Zapper techniques</p>
              </div>

              <p className="font-semibold text-dark-grey mb-2">Reported outcomes:</p>
              <ul className="space-y-2 mb-6">
                {studyOutcomes.map((outcome, index) => (
                  <li key={index} className="flex items-center justify-between text-gray-600">
                    <span>{outcome.label}:</span>
                    <span className="font-semibold text-dark-grey ml-2">{outcome.value}</span>
                  </li>
                ))}
              </ul>

              <p className="font-semibold text-dark-grey mb-2">Publication link:</p>
              <a
                href="https://www.sciencedirect.com/science/article/abs/pii/S1876382011001168"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gold hover:text-gold/80 underline transition-colors break-all block mb-4"
              >
                https://www.sciencedirect.com/science/article/abs/pii/S1876382011001168
              </a>
              <p className="font-semibold text-dark-grey mb-2">Additional reference:</p>
              <a
                href="https://benthamopen.com/FULLTEXT/TOEPIJ-8-1#r17"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gold hover:text-gold/80 underline transition-colors break-all"
              >
                https://benthamopen.com/FULLTEXT/TOEPIJ-8-1#r17
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* In summary */}
      <section ref={summaryRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                In summary
              </h2>
            </div>

            <div className="space-y-6 text-gray-600 leading-relaxed">
              <p>
                Bioresonance, applied as a frequency-based method, is a non-invasive complementary option that some individuals choose to explore in relation to muscle, joint and spine comfort, viewed holistically. It draws on observational insights and practitioner experience, with responses varying from person to person.
              </p>
              <p>
                Within a broader wellbeing perspective, this approach is often appreciated for its gentle, personalised nature and the way it can be thoughtfully included alongside conventional care as part of an integrative approach to physical comfort and balance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-gold/10 via-blue/10 to-gold/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
            Ready to Explore Musculoskeletal Support?
          </h2>
          <p className="text-xl text-dark-grey mb-8 max-w-3xl mx-auto">
            Discover how bioresonance therapy can provide complementary support for managing muscle, joint, and spine discomfort.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.sumupbookings.com/t-p-wellness-ltd"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-gold to-yellow hover:from-yellow hover:to-gold text-black font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
            >
              Schedule Consultation
            </a>
            <Link 
              to="/therapy/bioresonance-therapy"
              className="bg-transparent border-2 border-gold text-gold hover:bg-gold hover:text-black font-bold py-4 px-8 rounded-full transition-all duration-300"
            >
              Back to Bioresonance Therapy
            </Link>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 bg-gold hover:bg-gold/90 text-black p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 z-50"
        aria-label="Back to top"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    </div>
  );
};

export default MuscleJointSpine;
